import {
	ContextMenu,
	ContextMenuAnchorItem,
	ContextMenuDeleteItem,
	ContextMenuItem,
	ContextMenuLinkItem,
	FeedbackCreateDrawer,
	FeedbackIndexResource,
	FeedbackService,
	FeedbackUpdateDrawer,
	IDrawerDataListColumn,
	Id,
	PaginatedLaravelDrawerAutoDataList,
	useDynamicConfig,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { eventService } from "../../../shared/api/event/eventService";

interface Props {
	service: FeedbackService;
	canCreate: boolean;
	eventId: Id;
}

export const EventFeedbackList: FC<Props> = ({ canCreate, service, eventId }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { frontendUrl } = useDynamicConfig();
	const crudOperations = useLaravelCrudEntity(
		service,
		{
			entityName: t("Feedback"),
			messages: {
				create: t("Feedback was created."),
				update: t("Feedback was updated."),
				delete: t("Feedback was deleted."),
			},
		},
		[eventService.showKey(eventId)],
	);

	const dataListColumns: IDrawerDataListColumn<FeedbackIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
				sortable: true,
			},
			{
				heading: t("Actions"),
				type: "actions",
				size: "max-content",
				action: (feedback, openDrawer) => {
					return (
						<ContextMenu displayShortcutsAs="icon">
							<ContextMenuLinkItem
								label={t("Show feedback")}
								title={t("Show feedback")}
								shortcut="primary"
								icon="eye"
								to={`${feedback.id}/details`}
							/>
							{!feedback.isTemplate && (
								<ContextMenuItem
									title={t("Edit feedback")}
									label={t("Edit feedback")}
									action={openDrawer}
									icon="edit"
									shortcut="default"
								/>
							)}
							<ContextMenuAnchorItem
								label={t("Show preview")}
								title={t("Show preview")}
								icon="arrow_outward"
								href={`${frontendUrl}/feedbacks/${feedback.id}/preview`}
								target="_blank"
								rel="noreferrer"
							/>
							{!feedback.isTemplate && (
								<ContextMenuDeleteItem
									action={() => crudOperations.deleteEntity(feedback.id)}
									title={t("Delete feedback")}
									label={t("Delete feedback")}
									heading={t("Delete")}
									labels={{ close: t("Close"), confirm: t("Confirm") }}
								>
									{t("Are you sure you want to delete this feedback?")}
								</ContextMenuDeleteItem>
							)}
						</ContextMenu>
					);
				},
			},
		],
		[crudOperations, frontendUrl, t],
	);

	return (
		<PaginatedLaravelDrawerAutoDataList
			canUpdate={true}
			canCreate={canCreate}
			service={service}
			columns={dataListColumns}
			onRowClick={(feedback) => navigate(`${feedback.id}/details`)}
			renderCreateDrawer={(drawerProps) => (
				<FeedbackCreateDrawer {...drawerProps} onSubmit={crudOperations.createEntity} />
			)}
			renderUpdateDrawer={(drawerProps) => (
				<FeedbackUpdateDrawer
					{...drawerProps}
					service={service}
					onSubmit={(formValues) => crudOperations.updateEntity(drawerProps.id, formValues)}
				/>
			)}
			translations={{
				title: t("Feedback"),
				empty: t("No feedback available."),
			}}
			initialSorting={{ column: "name", direction: "asc" }}
			initialSearch={{ search: "", searchFields: [{ name: "feedback_translations.name", label: t("Name") }] }}
		/>
	);
};
